import React from "react";
import classnames from "classnames";

import {useEffect, useState} from "@wordpress/element";
import {ComboboxControl, Panel, PanelBody, PanelRow} from '@wordpress/components';
import {__} from '@wordpress/i18n';
import { useDispatch, dispatch } from '@wordpress/data';
import { more } from '@wordpress/icons';

import LoadingMask from '@woocommerce/base-components/loading-mask';
import Button from '@woocommerce/base-components/button';
import { VALIDATION_STORE_KEY, CART_STORE_KEY } from '@woocommerce/block-data';

import {useStoreCartVouchers} from "./use-store-cart-vouchers";
import './promotions-form.scss';

const AvailablePromotionsList = () => {
	const {getAllPromotions} = useStoreCartVouchers();

	return (
		<Panel className={classnames("wc-block-components-totals-promotion__available-list")}>
			<PanelBody title={__('Show all promotions', 'voucherify')} icon={ more } initialOpen={ false }>
				<PanelRow>
					<ul>
						{getAllPromotions()
							.filter(key => key !== null)
							.map((promotion) => (
								<li key={promotion}>{promotion}</li>
							))}
					</ul>
				</PanelRow>
			</PanelBody>
		</Panel>
	);
}

const PromotionsForm = () => {
	const {getValidPromotions, getAllPromotions, addPromotionsToList, isLoading} = useStoreCartVouchers();

	const [selectedPromotion, setSelectedPromotion] = useState('');
	const [inputFocused, setInputFocused] = useState(false);
	const [inputHasValue, setInputHasValue] = useState(false);

	const optionChanged = (promotionCode) => {
		setSelectedPromotion(promotionCode);

		promotionCode = promotionCode?.trim() ?? '';
		setInputHasValue(promotionCode !== '');

		[
			document.querySelector('.wc-block-components-totals-promotion__form .components-base-control__label'),
			document.querySelector('.wc-block-components-totals-promotion__form input.components-combobox-control__input[type="text"]')
		].map((element) => {
			if (element) {
				if (promotionCode !== '' || inputFocused) {
					element.classList.add('is-active');
				} else {
					element.classList.remove('is-active');
				}
			}
		});
	}

	const { setValidationErrors } = useDispatch( VALIDATION_STORE_KEY );

	const handleFocus = () => {
		setInputFocused(true);

		const input = document.querySelector('.wc-block-components-totals-promotion__form input.components-combobox-control__input[type="text"]');
		input.classList.add('is-active');

		const label = document.querySelector('.wc-block-components-totals-promotion__form .components-base-control__label');
		label.classList.add('is-active');
	};

	const handleBlur = () => {
		setInputFocused(false);
		if (!inputHasValue) {
			// Remove 'is-active' class from the label if input doesn't have value
			const input = document.querySelector('.wc-block-components-totals-promotion__form input.components-combobox-control__input[type="text"]');
			if (input) {
				input.classList.remove('is-active');
			}

			const label = document.querySelector('.wc-block-components-totals-promotion__form .components-base-control__label');
			if (label) {
				label.classList.remove('is-active');
			}
		}
	};

	useEffect(() => {
		// Add event listeners for input focus, blur, and change events
		const input = document.querySelector('.wc-block-components-totals-promotion__form input.components-combobox-control__input[type="text"]');
		if (input) {
			input.addEventListener('focus', handleFocus);
			input.addEventListener('blur', handleBlur);
		}

		// Clean up event listeners when the component is unmounted
		return () => {
			if (input) {
				input.removeEventListener('focus', handleFocus);
				input.removeEventListener('blur', handleBlur);
			}
		};
	}, [inputFocused, inputHasValue]);

	const handleError = (reason) => {
		setValidationErrors( {
			coupon: {
				message: decodeEntities( reason.message ),
				hidden: false,
			},
		} );
	}

	const handlePromotionSubmit = (e) => {
		e.preventDefault();

		addPromotionsToList(selectedPromotion)
			.then(response => {
				return dispatch( CART_STORE_KEY ).invalidateResolutionForStore();
			}).catch( reason => {
				handleError(reason);
				return Promise.resolve( false );
			});
	}

	return (
		<LoadingMask
			screenReaderLabel={ __(
				'Applying coupon…',
				'woocommerce'
			) }
			isLoading={ isLoading }
			showSpinner={ false }>
				<form
					className="wc-block-components-totals-promotion__form"
					id="wc-block-components-totals-promotion__form">
					<ComboboxControl
						label={__('Select available promotion', 'voucherify')}
						onChange={optionChanged}
						options={getValidPromotions().map(promo => {
							return {value: promo.id ?? '', label: promo.banner ?? ''};
						}).filter(promo => promo.value !== '' && promo.label !== '')}
						value={selectedPromotion}
					/>

					<div className={"wc-block-components-totals-promotion__buttons-container"}>
						<Button
							className="wc-block-components-totals-promotion__button"
							disabled={ isLoading || !selectedPromotion }
							showSpinner={ isLoading }
							onClick={ handlePromotionSubmit }
							type="submit"
						>
							{ __( 'Apply', 'woocommerce' ) }
						</Button>

					</div>
				</form>
			{getAllPromotions() ? <AvailablePromotionsList /> : null}
		</LoadingMask>
	);
}

export default PromotionsForm;
